import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { SpotlightCard } from '@atlaskit/onboarding';
import { PreloadedQuery } from 'react-relay';
import { GetPostSummarizerQuery } from 'graphql/__generated__/GetPostSummarizerQuery.graphql';
import useGetPostSummarizer from 'hooks/ai-forums/useGetPostSummarizer';

interface Props {
  queryRef: PreloadedQuery<GetPostSummarizerQuery>;
  onClose: () => void;
}

export const SummarizedResults = ({ queryRef, onClose }: Props) => {
  const { result, summary, keyPoints, sentiment } = useGetPostSummarizer({ queryRef });

  if (!result) {
    return (
      <div css={contentStyles}>
        <p>No summary available.</p>
      </div>
    );
  }

  return (
    <SpotlightCard headingLevel={2} actions={[{ text: 'Close', onClick: onClose, appearance: 'subtle' }]}>
      <div>
        <div css={headingContainerStyles}>
          <p css={headingIconStyles}>✨</p>
          <h3 css={headingStyles}>Post Summary</h3>
        </div>
        <p css={summaryTextStyles}>{summary}</p>
        <div css={keyPointsStyles}>
          <h4 css={keyPointsTitleStyles}>Key Points:</h4>
          <ul css={keyPointsListStyles}>
            {keyPoints.map((point) => (
              <li key={point} css={keyPointItemStyles}>
                {point}
              </li>
            ))}
          </ul>
        </div>
        {sentiment && (
          <div css={sentimentStyles}>
            <strong>Sentiment:</strong> {sentiment}
          </div>
        )}
      </div>
    </SpotlightCard>
  );
};

const contentStyles = css({
  paddingInlineStart: token('space.200'),
  paddingInlineEnd: token('space.200'),
  paddingBlockStart: token('space.200'),
  paddingBlockEnd: token('space.200'),
  maxWidth: '400px',
  minWidth: '300px',
});

const headingContainerStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: token('space.100'),
});

const headingStyles = css({
  margin: '0 0 16px 0',
  fontWeight: 'bold',
  color: token('color.text.inverse'),
});

const headingIconStyles = css({
  fontSize: '24px',
  color: token('color.text.inverse'),
});

const summaryTextStyles = css({
  margin: '0 0 16px 0',
  lineHeight: '1.5',
  color: token('color.text.inverse'),
  fontSize: '14px',
});

const keyPointsStyles = css({
  marginTop: token('space.200'),
});

const keyPointsTitleStyles = css({
  margin: '0 0 8px 0',
  fontSize: '14px',
  fontWeight: '600',
  color: token('color.text.inverse'),
});

const keyPointsListStyles = css({
  margin: 0,
  paddingLeft: token('space.200'),
});

const keyPointItemStyles = css({
  margin: '4px 0',
  fontSize: '13px',
  lineHeight: '1.4',
  color: token('color.text.inverse'),
});

const sentimentStyles = css({
  marginTop: token('space.200'),
  fontSize: '13px',
  color: token('color.text.inverse'),
});
